import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
    QuestionnaireDefinition,
    QuestionnaireEvent,
    QuestionnairePhase,
    QuestionnaireStep,
    QuestionnaireSubmitResult,
} from './types';

export interface UseQuestionnaireOptions<TAnswers> {
    definition: QuestionnaireDefinition<TAnswers>;
    initialAnswers: TAnswers;
    /** Whether a session already exists. Drives whether the auth phase is entered. */
    isAuthenticated: boolean;
    /**
     * Runs once the last step is answered and any auth wall is behind the
     * reader. What it resolves with rides on the `submitted` event.
     */
    onSubmit: (
        answers: TAnswers,
    ) =>
        | Promise<QuestionnaireSubmitResult | void>
        | QuestionnaireSubmitResult
        | void;
    /** Analytics seam. */
    onEvent?: (event: QuestionnaireEvent) => void;
}

export interface UseQuestionnaireResult<TAnswers> {
    phase: QuestionnairePhase;
    answers: TAnswers;
    /** Merges a patch into the answers, reporting the step as answered. */
    update: (patch: Partial<TAnswers>) => void;
    /**
     * Merges a patch without reporting it. For answers restored on the reader's
     * behalf, which are not answers they gave.
     */
    seed: (patch: Partial<TAnswers>) => void;
    /** The applicable steps, in order. Excludes any branched out by an answer. */
    steps: QuestionnaireStep<TAnswers>[];
    step: QuestionnaireStep<TAnswers> | undefined;
    /** 1-based, for the eyebrow. */
    stepNumber: number;
    totalSteps: number;
    canContinue: boolean;
    isFirstStep: boolean;
    next: () => void;
    back: () => void;
    /** Called by the host when the auth segment resolves. */
    completeAuth: () => void;
    /** Reports abandonment. Call from the host's dismiss path. */
    abandon: () => void;
    submitError: string | undefined;
    retrySubmit: () => void;
}

/**
 * How many applicable steps sit ahead of `key` in the definition.
 *
 * Used only when the step being shown has branched out of the flow: its
 * position is gone, but the definition still says where it was.
 */
const countStepsBefore = <TAnswers>(
    allSteps: QuestionnaireStep<TAnswers>[],
    applicableSteps: QuestionnaireStep<TAnswers>[],
    key: string | undefined,
): number => {
    const removedAt = allSteps.findIndex((entry) => entry.key === key);
    if (removedAt < 0) return applicableSteps.length;

    return applicableSteps.filter(
        (entry) =>
            allSteps.findIndex((item) => item.key === entry.key) < removedAt,
    ).length;
};

const resolveInitialPhase = (
    authPosition: QuestionnaireDefinition<unknown>['authPosition'],
    isAuthenticated: boolean,
): QuestionnairePhase =>
    authPosition === 'first' && !isAuthenticated ? 'auth' : 'questions';

/**
 * Runs a questionnaire: which step is showing, whether it may be left, and
 * where the account wall sits.
 *
 * Position is held as a step *key* rather than an index. A step can branch
 * itself out of the flow when an earlier answer changes, and an index would
 * then silently point at a different question — which is the bug the previous
 * implementation worked around by threading an overridden flow list through
 * every advance.
 */
export const useQuestionnaire = <TAnswers>({
    definition,
    initialAnswers,
    isAuthenticated,
    onSubmit,
    onEvent,
}: UseQuestionnaireOptions<TAnswers>): UseQuestionnaireResult<TAnswers> => {
    const [answers, setAnswers] = useState<TAnswers>(initialAnswers);

    /**
     * The answers as of right now, rather than as of the last render.
     *
     * An answer can decide whether a later step belongs in the flow at all, and
     * a step that commits and advances in one action does both before React has
     * re-rendered. Reading the flow from render state there would compute it
     * from the answers as they were a moment ago, and step straight past a
     * question the choice had just brought into being.
     */
    const answersRef = useRef<TAnswers>(initialAnswers);
    const [phase, setPhase] = useState<QuestionnairePhase>(() =>
        resolveInitialPhase(definition.authPosition, isAuthenticated),
    );
    // The first step that applies, not the first step declared. A flow can open
    // with a step that only some readers see — asking which project a brief is
    // for, say — and starting on one that is not in the flow leaves navigation
    // resolving against a key that is not there.
    const [currentKey, setCurrentKey] = useState<string | undefined>(
        () =>
            definition.steps.find(
                (entry) => entry.isApplicable?.(initialAnswers) ?? true,
            )?.key,
    );
    const [submitError, setSubmitError] = useState<string | undefined>();

    // Read through a ref so a caller passing a fresh closure each render does
    // not re-fire the view event or tear down an in-flight submit.
    const eventRef = useRef(onEvent);
    eventRef.current = onEvent;
    const submitRef = useRef(onSubmit);
    submitRef.current = onSubmit;

    const emit = useCallback((event: QuestionnaireEvent) => {
        eventRef.current?.(event);
    }, []);

    const steps = useMemo(
        () =>
            definition.steps.filter(
                (step) => step.isApplicable?.(answers) ?? true,
            ),
        [definition.steps, answers],
    );

    // If the current step branched itself out, land on the last applicable step
    // that precedes it. Falling back to the end of the list instead would carry
    // the reader past every question between here and there.
    const rawIndex = steps.findIndex((entry) => entry.key === currentKey);
    const index =
        rawIndex >= 0
            ? rawIndex
            : Math.max(
                  countStepsBefore(definition.steps, steps, currentKey) - 1,
                  0,
              );
    const step = steps[index];

    /**
     * Whether the reader has moved themselves.
     *
     * Until they have, the opening position stays pinned to the first step that
     * applies. A flow can learn something after it opens — which projects the
     * reader owns, say — and a step that becomes applicable a moment later
     * would otherwise be behind them before they ever saw it.
     */
    const hasNavigated = useRef(false);
    useEffect(() => {
        if (hasNavigated.current) return;
        const first = steps[0]?.key;
        if (first && first !== currentKey) setCurrentKey(first);
    }, [steps, currentKey]);

    const openedRef = useRef(false);
    useEffect(() => {
        if (openedRef.current) return;
        openedRef.current = true;
        emit({
            type: 'opened',
            questionnaireId: definition.id,
            authPosition: definition.authPosition,
        });
        if (definition.authPosition === 'first' && !isAuthenticated) {
            emit({ type: 'auth-required', questionnaireId: definition.id });
        }
    }, [definition.id, definition.authPosition, isAuthenticated, emit]);

    const viewedRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (phase !== 'questions' || !step || viewedRef.current === step.key) {
            return;
        }
        viewedRef.current = step.key;
        emit({
            type: 'step-viewed',
            questionnaireId: definition.id,
            stepKey: step.key,
            stepNumber: index + 1,
            totalSteps: steps.length,
        });
    }, [phase, step, index, steps.length, definition.id, emit]);

    /**
     * Writes answers without reporting anyone as having answered.
     *
     * Restoring what a reader told us last time is not the reader answering.
     * Seeding through `update` reported a `step-answered` against whichever
     * question happened to be on screen at the time — which is the first one,
     * before they have read it — so every returning reader logged an answer to
     * a question they had not touched, and step one's completion rate read as
     * if nobody ever dropped out there.
     */
    const seed = useCallback((patch: Partial<TAnswers>) => {
        answersRef.current = { ...answersRef.current, ...patch };
        setAnswers(answersRef.current);
    }, []);

    /**
     * Steps already reported as answered. A step reports once, on the first
     * answer the reader gives it: a typed answer calls `update` on every
     * keystroke, and a step changed after going back to it is still one step
     * answered.
     */
    const answeredStepsRef = useRef(new Set<string>());

    const update = useCallback(
        (patch: Partial<TAnswers>) => {
            seed(patch);
            if (!step || answeredStepsRef.current.has(step.key)) return;
            answeredStepsRef.current.add(step.key);
            emit({
                type: 'step-answered',
                questionnaireId: definition.id,
                stepKey: step.key,
            });
        },
        [seed, step, definition.id, emit],
    );

    const runSubmit = useCallback(
        async (finalAnswers: TAnswers) => {
            setPhase('submitting');
            setSubmitError(undefined);
            try {
                const result = await submitRef.current(finalAnswers);
                const submissionID = result ? result.submissionID : undefined;
                emit({
                    type: 'submitted',
                    questionnaireId: definition.id,
                    ...(submissionID === undefined ? {} : { submissionID }),
                });
                setPhase('done');
            } catch (error) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Something went wrong. Please try again.';
                emit({
                    type: 'submit-failed',
                    questionnaireId: definition.id,
                    message,
                });
                setSubmitError(message);
                // Back to the questions so the brief is still in front of the
                // reader and the retry is one button rather than a re-entry.
                setPhase('questions');
            }
        },
        [definition.id, emit],
    );

    const next = useCallback(() => {
        hasNavigated.current = true;
        const liveSteps = definition.steps.filter(
            (entry) => entry.isApplicable?.(answersRef.current) ?? true,
        );
        // A step can remove itself as it is left, so the key being navigated from
        // is not always still in the flow. Fall back the same way the render
        // does rather than resolving to nowhere.
        const rawLiveIndex = liveSteps.findIndex(
            (entry) => entry.key === currentKey,
        );
        const liveIndex =
            rawLiveIndex >= 0
                ? rawLiveIndex
                : Math.max(
                      countStepsBefore(
                          definition.steps,
                          liveSteps,
                          currentKey,
                      ) - 1,
                      0,
                  );
        const isLast = liveIndex >= liveSteps.length - 1;

        if (!isLast) {
            setCurrentKey(liveSteps[liveIndex + 1]?.key);
            return;
        }

        if (definition.authPosition === 'before-submit' && !isAuthenticated) {
            emit({ type: 'auth-required', questionnaireId: definition.id });
            setPhase('auth');
            return;
        }

        void runSubmit(answersRef.current);
    }, [
        currentKey,
        definition.steps,
        definition.authPosition,
        definition.id,
        isAuthenticated,
        runSubmit,
        emit,
    ]);

    const back = useCallback(() => {
        if (index <= 0) return;
        hasNavigated.current = true;
        if (step) {
            emit({
                type: 'step-back',
                questionnaireId: definition.id,
                stepKey: step.key,
            });
        }
        setCurrentKey(steps[index - 1]?.key);
    }, [index, steps, step, definition.id, emit]);

    const completeAuth = useCallback(() => {
        emit({ type: 'auth-completed', questionnaireId: definition.id });

        // Auth sitting last means the reader has already answered everything,
        // so clearing the wall is the submit rather than a return to the form.
        if (definition.authPosition === 'before-submit') {
            void runSubmit(answersRef.current);
            return;
        }
        setPhase('questions');
    }, [definition.authPosition, definition.id, runSubmit, emit]);

    const abandon = useCallback(() => {
        if (phase === 'done' || phase === 'submitting') return;
        emit({
            type: 'abandoned',
            questionnaireId: definition.id,
            lastStepKey: phase === 'auth' ? 'auth' : (step?.key ?? 'unknown'),
        });
    }, [phase, step, definition.id, emit]);

    const retrySubmit = useCallback(() => {
        void runSubmit(answersRef.current);
    }, [runSubmit]);

    return {
        phase,
        answers,
        update,
        seed,
        steps,
        step,
        stepNumber: index + 1,
        totalSteps: steps.length,
        canContinue: step?.isComplete?.(answers) ?? true,
        isFirstStep: index === 0,
        next,
        back,
        completeAuth,
        abandon,
        submitError,
        retrySubmit,
    };
};
